/** Shared chrome for the participant journey (Interview + Withdraw): skip link, header,
 * optional progress bar, and a heading that takes focus whenever it (re)appears, so a
 * screen-reader or keyboard user always lands on the new screen's title instead of losing
 * their place. Depends on `title`, not just mount: some screens (Finish's "Nearly done" to
 * "Thank you", Withdraw's form to "You have withdrawn") swap title within the same mounted
 * component instance rather than remounting, so a mount-only effect would miss them. */
import { useEffect, useRef } from 'react';

export function Shell({ title, children, progress }) {
  const h1 = useRef(null);
  useEffect(() => { h1.current?.focus(); }, [title]);
  return (
    <main className="min-h-screen bg-white text-slate-900" style={{ fontSize: '1.15rem' }}>
      <a href="#content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white p-2 z-50">Skip to content</a>
      <header className="on-brand-navy bg-brand-navy text-white px-5 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-brand-teal flex items-center justify-center shrink-0" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" /><path d="M19 11a7 7 0 0 1-14 0" /><path d="M12 18v3" /></svg>
            </span>
            <span className="font-bold text-lg">DI Voices</span>
          </span>
          <span className="text-sm text-teal-400">Diverse Integration</span>
        </div>
        {progress && (
          <div className="max-w-2xl mx-auto mt-3">
            <div className="text-sm mb-1" aria-hidden="true">{progress.label}</div>
            <div className="h-2 bg-black/30 rounded" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.n} aria-label={progress.label}>
              <div className="h-2 bg-brand-amber rounded" style={{ width: `${(100 * progress.n) / progress.total}%` }} />
            </div>
          </div>
        )}
      </header>
      <div id="content" className="max-w-2xl mx-auto px-5 py-6">
        {title && <h1 ref={h1} tabIndex={-1} className="text-2xl md:text-3xl font-bold mb-4 outline-none">{title}</h1>}
        {children}
      </div>
    </main>
  );
}
